import React from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

const TeamBuilderConfig = ({
  numTeams,
  setNumTeams,
  teamGroupings,
  setTeamGroupings,
  selectedGroups,
  setSelectedGroups,
  includeUnscored,
  setIncludeUnscored,
  availableGroups,
}) => {
  const handleNumTeamsChange = (value) => {
    const num = parseInt(value, 10);
    if (num >= 2 && num <= 10) {
      setNumTeams(num);
    }
  };

  const handleGroupingModeChange = (index, mode) => {
    const updated = [...teamGroupings];
    updated[index].mode = mode;
    setTeamGroupings(updated);
  };

  const handleGroupingRangeChange = (index, field, value) => {
    const updated = [...teamGroupings];
    updated[index][field] = parseInt(value, 10);
    setTeamGroupings(updated);
  };

  const addGrouping = () => {
    // Find the next available range
    const lastGrouping = teamGroupings[teamGroupings.length - 1];
    const nextStart = lastGrouping ? lastGrouping.endTeam + 1 : 1;

    if (nextStart <= numTeams) {
      setTeamGroupings([
        ...teamGroupings,
        {
          startTeam: nextStart,
          endTeam: numTeams,
          mode: "balanced",
        },
      ]);
    }
  };

  const removeGrouping = (index) => {
    if (teamGroupings.length > 1) {
      const updated = teamGroupings.filter((_, i) => i !== index);
      setTeamGroupings(updated);
    }
  };

  const toggleGroup = (group) => {
    if (selectedGroups.includes(group)) {
      setSelectedGroups(selectedGroups.filter((g) => g !== group));
    } else {
      setSelectedGroups([...selectedGroups, group]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Number of Teams */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of Teams
          </label>
          <select
            value={numTeams}
            onChange={(e) => handleNumTeamsChange(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
          >
            {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>
                {n} Teams
              </option>
            ))}
          </select>
        </div>

        {/* Filter by Groups */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Filter by Groups
          </label>
          {availableGroups.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No groups defined</div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {availableGroups.map((group) => (
                <button
                  key={group}
                  onClick={() => toggleGroup(group)}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    selectedGroups.includes(group)
                      ? "bg-slate-100 border-slate-500 text-slate-700"
                      : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {group}
                </button>
              ))}
              {selectedGroups.length > 0 && (
                <button
                  onClick={() => setSelectedGroups([])}
                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Include Unscored */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Players Without Scores
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={includeUnscored}
              onChange={(e) => setIncludeUnscored(e.target.checked)}
              className="rounded border-gray-300 text-slate-600 focus:ring-slate-500"
            />
            <span className="ml-2 text-sm text-gray-600">
              Include unscored players
            </span>
          </label>
        </div>
      </div>

      {/* Team Groupings */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Distribution Mode by Team Range
          </label>
          {teamGroupings.length < numTeams && (
            <button
              onClick={addGrouping}
              className="flex items-center text-sm text-slate-600 hover:text-slate-800"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Range
            </button>
          )}
        </div>

        <div className="space-y-2">
          {teamGroupings.map((grouping, index) => (
            <div
              key={index}
              className="flex items-center gap-3 bg-white border border-gray-200 rounded-md p-2"
            >
              <span className="text-sm text-gray-600">Teams</span>
              <select
                value={grouping.startTeam}
                onChange={(e) =>
                  handleGroupingRangeChange(index, "startTeam", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {Array.from({ length: numTeams }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600">to</span>
              <select
                value={grouping.endTeam}
                onChange={(e) =>
                  handleGroupingRangeChange(index, "endTeam", e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {Array.from({ length: numTeams }, (_, i) => i + 1)
                  .filter((n) => n >= grouping.startTeam)
                  .map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
              </select>
              <span className="text-sm text-gray-600">:</span>
              <select
                value={grouping.mode}
                onChange={(e) =>
                  handleGroupingModeChange(index, e.target.value)
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm flex-1"
              >
                <option value="ranked">Ranked (Best players first)</option>
                <option value="balanced">Balanced (Mixed skill levels)</option>
              </select>
              {teamGroupings.length > 1 && (
                <button
                  onClick={() => removeGrouping(index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-500 mt-2">
          <strong>Ranked:</strong> Top players go to Team 1, next to Team 2,
          etc. <strong>Balanced:</strong> Mix of skill levels on each team
          (snake draft).
        </p>
      </div>
    </div>
  );
};

export { TeamBuilderConfig };
